import { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import {
  Send,
  Paperclip,
  X,
  FileText,
  Image as ImageIcon,
  Download,
  File as FileIcon,
  Loader2,
  Trash2,
} from "lucide-react";

const ALLOWED_EXTS = ["pdf", "jpg", "jpeg", "png", "doc", "docx", "xls", "xlsx", "txt", "csv"];
const MAX_SIZE = 10 * 1024 * 1024;

function bytesFmt(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function extIcon(ext) {
  if (["jpg", "jpeg", "png"].includes(ext)) return ImageIcon;
  if (ext === "pdf") return FileText;
  return FileIcon;
}

function formatTime(iso) {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return iso;
  }
}

const AttachmentChip = ({ att, onDownload, onRemove }) => {
  const Icon = extIcon(att.ext);
  return (
    <div className="inline-flex items-center gap-2 border border-slate-200 bg-white rounded-md px-2 py-1.5 text-xs text-slate-700 max-w-full">
      <Icon className="w-3.5 h-3.5 text-slate-500 shrink-0" />
      <span className="truncate max-w-[180px]" title={att.filename}>
        {att.filename}
      </span>
      <span className="text-slate-400 text-[10px]">{bytesFmt(att.size)}</span>
      {onDownload && (
        <button
          type="button"
          onClick={onDownload}
          className="text-slate-500 hover:text-slate-900"
          data-testid={`attachment-download-${att.id}`}
        >
          <Download className="w-3.5 h-3.5" />
        </button>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="text-slate-400 hover:text-rose-600"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

async function downloadAttachment(att) {
  try {
    const res = await api.get(`/files/${att.id}`, { responseType: "blob" });
    const blob = new Blob([res.data], { type: att.content_type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = att.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch (err) {
    toast.error(formatApiError(err));
  }
}

export const ChatWindow = ({ clientId, currentRole, className = "" }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [pending, setPending] = useState([]); // uploaded but not-yet-sent attachments
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = useCallback(
    async (silent = false) => {
      if (!clientId) return;
      try {
        const { data } = await api.get(`/messages?client_id=${clientId}`);
        setMessages(data);
      } catch (err) {
        if (!silent) toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    },
    [clientId]
  );

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // Auto-refresh every 4s
  useEffect(() => {
    if (!clientId) return undefined;
    const id = setInterval(() => load(true), 4000);
    return () => clearInterval(id);
  }, [clientId, load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  const handleDeleteMessage = useCallback(async (messageId) => {
    if (!window.confirm("Delete this message? This cannot be undone.")) return;
    // Optimistic removal; revert on failure.
    let prev;
    setMessages((ms) => {
      prev = ms;
      return ms.filter((m) => m.id !== messageId);
    });
    try {
      await api.delete(`/messages/${messageId}`);
    } catch (err) {
      toast.error(formatApiError(err));
      if (prev) setMessages(prev);
    }
  }, []);

  const onPickFiles = () => fileInputRef.current?.click();

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const newOnes = [];
      for (const f of files) {
        const ext = (f.name.split(".").pop() || "").toLowerCase();
        if (!ALLOWED_EXTS.includes(ext)) {
          toast.error(`.${ext} is not allowed`);
          continue;
        }
        if (f.size > MAX_SIZE) {
          toast.error(`${f.name} exceeds 10 MB`);
          continue;
        }
        const form = new FormData();
        form.append("file", f);
        const { data } = await api.post("/uploads", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newOnes.push(data);
      }
      setPending((cur) => [...cur, ...newOnes]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePending = (id) => {
    setPending((cur) => cur.filter((p) => p.id !== id));
  };

  const send = async () => {
    if (sending) return;
    const trimmed = text.trim();
    if (!trimmed && pending.length === 0) return;
    setSending(true);
    try {
      await api.post(`/messages?client_id=${clientId}`, {
        text: trimmed,
        attachment_ids: pending.map((p) => p.id),
      });
      setText("");
      setPending([]);
      await load(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className={`flex flex-col bg-white border border-slate-200 rounded-md overflow-hidden ${className}`} data-testid="chat-window">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-50/40" data-testid="chat-messages">
        {loading && (
          <div className="text-center text-slate-400 text-sm py-10">Loading conversation…</div>
        )}
        {!loading && messages.length === 0 && (
          <div className="text-center text-slate-400 text-sm py-16">
            No messages yet. Start the conversation below.
          </div>
        )}
        {!loading && messages.length > 0 && messages.map((m) => {
          const mine = m.sender_role === currentRole;
          return (
            <div
              key={m.id}
              className={`flex mb-3 ${mine ? "justify-end" : "justify-start"}`}
              data-testid={`msg-${m.id}`}
            >
              <div className={`max-w-[75%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                <div
                  className={`px-4 py-2.5 rounded-md text-sm leading-relaxed shadow-sm break-words ${
                    mine
                      ? "bg-slate-900 text-white rounded-br-none"
                      : "bg-white text-slate-900 border border-slate-200 rounded-bl-none"
                  }`}
                >
                  {m.text && <div className="whitespace-pre-wrap">{m.text}</div>}
                  {m.attachments && m.attachments.length > 0 && (
                    <div className={`flex flex-wrap gap-2 ${m.text ? "mt-2" : ""}`}>
                      {m.attachments.map((att) => (
                        <div
                          key={att.id}
                          className={`inline-flex items-center gap-2 border rounded-md px-2 py-1.5 text-xs max-w-full ${
                            mine
                              ? "bg-slate-800 border-slate-700 text-slate-100"
                              : "bg-slate-50 border-slate-200 text-slate-700"
                          }`}
                        >
                          {(() => {
                            const Icon = extIcon(att.ext);
                            return <Icon className="w-3.5 h-3.5 shrink-0 opacity-70" />;
                          })()}
                          <span className="truncate max-w-[180px]" title={att.filename}>
                            {att.filename}
                          </span>
                          <span className="text-[10px] opacity-60">{bytesFmt(att.size)}</span>
                          <button
                            type="button"
                            onClick={() => downloadAttachment(att)}
                            className={`shrink-0 ${mine ? "text-slate-200 hover:text-white" : "text-slate-500 hover:text-slate-900"}`}
                            data-testid={`msg-attachment-download-${att.id}`}
                            aria-label="Download attachment"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-[10px] text-slate-400 mt-1 px-1 flex items-center gap-2">
                  <span>
                    {mine ? "You" : m.sender_name} · {formatTime(m.created_at)}
                  </span>
                  {(mine || currentRole === "admin") && (
                    <button
                      type="button"
                      onClick={() => handleDeleteMessage(m.id)}
                      className="text-slate-400 hover:text-rose-600 transition-colors"
                      data-testid={`msg-delete-${m.id}`}
                      aria-label="Delete message"
                      title="Delete message"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Pending attachments preview */}
      {pending.length > 0 && (
        <div className="border-t border-slate-200 bg-slate-50 px-3 py-2 flex flex-wrap gap-2" data-testid="pending-attachments">
          {pending.map((p) => (
            <AttachmentChip
              key={p.id}
              att={p}
              onRemove={() => removePending(p.id)}
            />
          ))}
        </div>
      )}

      {/* Composer */}
      <div className="border-t border-slate-200 bg-white p-3 flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => uploadFiles(e.target.files)}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.txt,.csv"
          data-testid="chat-file-input"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onPickFiles}
          disabled={uploading}
          className="text-slate-500 hover:text-slate-900 shrink-0"
          data-testid="chat-attach-btn"
          title="Attach files (max 10 MB each)"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
        </Button>
        <Textarea
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Type a message… (Enter to send, Shift+Enter for new line)"
          className="flex-1 resize-none rounded-md border-slate-300 min-h-[42px] max-h-32"
          data-testid="chat-text-input"
        />
        <Button
          type="button"
          onClick={send}
          disabled={sending || (!text.trim() && pending.length === 0)}
          className="bg-slate-900 hover:bg-slate-800 text-white shrink-0"
          data-testid="chat-send-btn"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
};

export default ChatWindow;
// Suppress unused-import warning for API_BASE (may be used later)
export { API_BASE };
